'use client';

import { FC, lazy } from 'react';

import LazySection from '@/components/LazySection';
import { RecommendSectionDto } from '@/components/Partnership/Recommend/types';
import { ContactUsDto } from '@/components/shared/Header/types';
import { LatestBlogPostsData } from '@/components/shared/LatestBlogPosts/types';
import { WebsiteAuditFormDto } from '@/components/WebsiteProdPage/ScheduleMeeting/types';
import { BreadcrumbItem } from '@/lib/breadcrumbs/types';

import css from './style.module.scss';
import { CompanyLogosBlockDto, DynamicPageDto } from './types';

const IndustryHero = lazy(() => import('@/components/industry-pages/Hero'));
const IndustriesLobbyHero = lazy(() => import('@/components/industry-pages/IndustriesLobbyHero'));
const CasesSlider = lazy(() => import('@/components/service-pages/CasesSlider'));
const CmoHeadacheWithCircle = lazy(
	() => import('@/components/industry-pages/CmoHeadacheWithCircle')
);
const TestimonialsSection = lazy(() => import('@/components/service-pages/TestimonialsSection'));
const CompanyLogos = lazy(() => import('@/components/industry-pages/CompanyLogos'));
const MediaParallaxCards = lazy(() => import('@/components/service-pages/MediaParallaxCards'));
const Faq = lazy(() => import('@/components/service-pages/Faq'));
const ScrollStackSection = lazy(() => import('@/components/industry-pages/ScrollStackSection'));
const IndustriesHeadingBlock = lazy(
	() => import('@/components/industry-pages/IndustriesHeadingBlock')
);
const CrowCards = lazy(() => import('@/components/Partnership/CrowCards'));
const ReferralSection = lazy(() => import('@/components/Partnership/ReferralSection'));
const IndustriesLobbySlider = lazy(
	() => import('@/components/industry-pages/IndustriesLobbySlider')
);
const LatestBlogPosts = lazy(() => import('@/components/shared/LatestBlogPosts'));
const CustomCursorProvider = lazy(() => import('@/components/CustomCursorProvider'));

const components: Record<string, FC<any>> = {
	'industry.hero-industry': IndustryHero,
	'industry.cmo-headache-with-circle': CmoHeadacheWithCircle,
	'website-prod-page.cases-slider': CasesSlider,
	'service-pages.testimonials-section': TestimonialsSection,
	'industry.company-logos': CompanyLogos,
	'industry.scroll-stack-section': ScrollStackSection,
	'website-prod-page.focus-matters': MediaParallaxCards,
	'website-prod-page.faq-section': Faq,
	'partnership.heading-block': IndustriesHeadingBlock,
	'partnership.crow-cards': CrowCards,
	'partnership.referral': ReferralSection,
	'industry.industries-lobby-slider': IndustriesLobbySlider
};

interface Props {
	data: DynamicPageDto & { latestBlogPosts?: LatestBlogPostsData };
	companyLogosData?: CompanyLogosBlockDto;
	dataRecommend?: RecommendSectionDto;
	contactUsData?: ContactUsDto;
	websiteAuditFormData?: WebsiteAuditFormDto;
	isLobbyPage?: boolean;
	breadcrumbItems?: BreadcrumbItem[];
}

const IndustryPage: FC<Props> = ({
	data,
	companyLogosData,
	dataRecommend,
	contactUsData,
	websiteAuditFormData,
	isLobbyPage,
	breadcrumbItems
}) => (
	<div className={css.container}>
		{data?.content?.length
			? data?.content?.map((block: any, index: number) => {
					const Component = components[block.__component as keyof typeof components];

					if (!Component) return null;

					const isHero = block.__component === 'industry.hero-industry';
					const isCompanyLogos = block.__component === 'industry.company-logos';

					const element = (
						<Component
							key={`block_${index}`}
							isIndustry
							data={block}
							withDivider={block.withDivider}
							dataRecommend={dataRecommend}
							contactUsData={contactUsData}
							websiteAuditFormData={isHero ? websiteAuditFormData : undefined}
							breadcrumbItems={isHero ? breadcrumbItems : undefined}
							{...(isCompanyLogos && companyLogosData ? { companyLogosData } : {})}
						/>
					);

					const heroElement = isLobbyPage ? (
						<IndustriesLobbyHero
							key={`block_${index}`}
							data={block}
							websiteAuditFormData={websiteAuditFormData}
							breadcrumbItems={breadcrumbItems}
						/>
					) : (
						element
					);

					return isHero ? heroElement : <LazySection key={`block_${index}`}>{element}</LazySection>;
				})
			: null}

		{data?.latestBlogPosts ? (
			<LazySection observerMarginBottom={1000}>
				<CustomCursorProvider mousemoveObserverSelector="#industry-page-latest-blog-posts">
					<LatestBlogPosts htmlId="industry-page-latest-blog-posts" data={data.latestBlogPosts} />
				</CustomCursorProvider>
			</LazySection>
		) : null}
	</div>
);

export default IndustryPage;
